/**
 * 系统设置页面
 *
 * 包含个人信息、用户管理、报警规则、系统配置等功能
 */

import { useState, useEffect } from 'react'
import {
  Tabs,
  Card,
  Form,
  Input,
  Button,
  Table,
  Space,
  Modal,
  Select,
  Switch,
  InputNumber,
  Popconfirm,
  message,
  Tag,
  Typography,
  Divider,
  Row,
  Col,
  Descriptions,
  Avatar,
} from 'antd'
import {
  UserOutlined,
  TeamOutlined,
  BellOutlined,
  SettingOutlined,
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
  EnvironmentOutlined,
  EyeOutlined,
  KeyOutlined,
  ReloadOutlined,
  SearchOutlined,
} from '@ant-design/icons'
import { useUserStore, useUIStore } from '@/store/useDeviceStore'
import { get, post, put, del } from '@/utils/request'

const { Title, Text } = Typography
const { TextArea } = Input

// API 基础地址（仅用于显示）
const API_BASE = import.meta.env.VITE_API_BASE || window.location.origin

// ============================================================
// 个人信息 Tab
// ============================================================

function ProfileTab() {
  const { username, role, userId } = useUserStore()
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const isAdmin = role === 'admin'

  const handleChangePassword = async (values: any) => {
    setLoading(true)
    try {
      await post('/api/v1/auth/change-password', values)
      message.success('密码修改成功')
      form.resetFields()
    } catch (error) {
      // 错误已在request中处理
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="settings-profile-layout">
      <Card className="settings-profile-card" bordered={false}>
        <div className="settings-profile-hero">
          <div className="settings-profile-hero-main">
            <Avatar
              size={68}
              icon={<UserOutlined />}
              className="settings-profile-avatar"
            />
            <div className="settings-profile-hero-text">
              <Text className="settings-profile-eyebrow">当前账号</Text>
              <Title level={3} style={{ margin: 0 }}>
                {username}
              </Title>
              <Text type="secondary">
                用于系统登录、权限校验和控制操作审计
              </Text>
            </div>
          </div>
          <Tag color={isAdmin ? 'red' : 'blue'} className="settings-profile-role-tag">
            {isAdmin ? '管理员' : '普通用户'}
          </Tag>
        </div>

        <div className="settings-profile-summary">
          <div className="settings-profile-summary-item">
            <Text type="secondary">用户名</Text>
            <Text strong>{username}</Text>
          </div>
          <div className="settings-profile-summary-item">
            <Text type="secondary">角色</Text>
            <Text strong>{isAdmin ? '管理员' : '普通用户'}</Text>
          </div>
          <div className="settings-profile-summary-item">
            <Text type="secondary">用户 ID</Text>
            <Text code>{userId}</Text>
          </div>
        </div>

        <Divider style={{ margin: '24px 0' }} />

        <div className="settings-profile-note">
          <Text type="secondary">
            当前账号信息由系统认证模块维护。如需调整权限，请在“用户管理”中由管理员处理。
          </Text>
        </div>
      </Card>

      <Card
        className="settings-profile-card settings-security-card"
        bordered={false}
        title={
          <Space size={10}>
            <KeyOutlined />
            <span>密码安全</span>
          </Space>
        }
      >
        <Space direction="vertical" size={4} style={{ width: '100%', marginBottom: 20 }}>
          <Text strong>修改登录密码</Text>
          <Text type="secondary">
            建议使用不少于 6 位的复杂密码，修改后新密码立即生效。
          </Text>
        </Space>

        <Form
          form={form}
          layout="vertical"
          onFinish={handleChangePassword}
          className="settings-security-form"
        >
          <Form.Item
            name="old_password"
            label="原密码"
            rules={[{ required: true, message: '请输入原密码' }]}
          >
            <Input.Password placeholder="请输入当前登录密码" />
          </Form.Item>

          <Form.Item
            name="new_password"
            label="新密码"
            rules={[
              { required: true, message: '请输入新密码' },
              { min: 6, message: '密码至少6个字符' },
            ]}
          >
            <Input.Password placeholder="请输入新的登录密码" />
          </Form.Item>

          <Form.Item
            name="confirm_password"
            label="确认新密码"
            dependencies={['new_password']}
            rules={[
              { required: true, message: '请确认新密码' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('new_password') === value) {
                    return Promise.resolve()
                  }
                  return Promise.reject(new Error('两次输入的密码不一致'))
                },
              }),
            ]}
          >
            <Input.Password placeholder="请再次输入新密码" />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0 }}>
            <Button type="primary" htmlType="submit" loading={loading} size="large">
              修改密码
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  )
}

// ============================================================
// 用户管理 Tab
// ============================================================

interface UserListItem {
  user_id: string
  username: string
  role: 'admin' | 'user'
  enabled: boolean
  created_at?: string
  last_login?: string
}

function UserManageTab() {
  const { userId } = useUserStore()
  const [users, setUsers] = useState<UserListItem[]>([])
  const [loading, setLoading] = useState(false)
  const [keyword, setKeyword] = useState('')
  const [roleFilter, setRoleFilter] = useState<'all' | 'admin' | 'user'>('all')
  const [enabledFilter, setEnabledFilter] = useState<'all' | 'true' | 'false'>('all')
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 })
  const [createVisible, setCreateVisible] = useState(false)
  const [detailVisible, setDetailVisible] = useState(false)
  const [resetVisible, setResetVisible] = useState(false)
  const [detailLoading, setDetailLoading] = useState(false)
  const [selectedUser, setSelectedUser] = useState<UserListItem | null>(null)
  const [createForm] = Form.useForm()
  const [resetForm] = Form.useForm()

  const loadUsers = async (
    page = pagination.current,
    pageSize = pagination.pageSize,
    currentKeyword = keyword,
    currentRole = roleFilter,
    currentEnabled = enabledFilter
  ) => {
    setLoading(true)
    try {
      const params = new URLSearchParams({
        page: String(page),
        page_size: String(pageSize),
      })
      if (currentKeyword.trim()) {
        params.set('keyword', currentKeyword.trim())
      }
      if (currentRole !== 'all') {
        params.set('role', currentRole)
      }
      if (currentEnabled !== 'all') {
        params.set('enabled', currentEnabled)
      }

      const data = await get<{ total: number; items: UserListItem[] }>(`/api/v1/users?${params.toString()}`)
      setUsers(data.items)
      setPagination({ current: page, pageSize, total: data.total })
    } catch (error) {
      // 错误已在request中处理
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadUsers()
  }, [])

  const handleCreate = async (values: any) => {
    try {
      await post('/api/v1/users', values)
      message.success('用户创建成功')
      setCreateVisible(false)
      createForm.resetFields()
      loadUsers(1, pagination.pageSize)
    } catch (error) {
      // 错误已在request中处理
    }
  }

  const handleDelete = async (targetUserId: string) => {
    try {
      await del(`/api/v1/users/${targetUserId}`)
      message.success('用户已删除')
      loadUsers()
    } catch (error) {
      // 错误已在request中处理
    }
  }

  const handleRoleChange = async (targetUserId: string, role: string) => {
    try {
      await put(`/api/v1/users/${targetUserId}/role`, { role })
      message.success('角色修改成功')
      loadUsers()
    } catch (error) {
      // 错误已在request中处理
    }
  }

  const handleEnabledChange = async (targetUserId: string, enabled: boolean) => {
    try {
      await put(`/api/v1/users/${targetUserId}/enabled`, { enabled })
      message.success(enabled ? '账号已启用' : '账号已禁用')
      loadUsers()
      if (selectedUser?.user_id === targetUserId) {
        setSelectedUser((prev) => (prev ? { ...prev, enabled } : prev))
      }
    } catch (error) {
      // 错误已在request中处理
    }
  }

  const openDetail = async (targetUserId: string) => {
    setDetailLoading(true)
    setDetailVisible(true)
    try {
      const data = await get<UserListItem>(`/api/v1/users/${targetUserId}`)
      setSelectedUser(data)
    } catch (error) {
      setDetailVisible(false)
    } finally {
      setDetailLoading(false)
    }
  }

  const openResetPassword = (record: UserListItem) => {
    setSelectedUser(record)
    resetForm.resetFields()
    setResetVisible(true)
  }

  const handleResetPassword = async (values: { new_password: string }) => {
    if (!selectedUser) return
    try {
      await put(`/api/v1/users/${selectedUser.user_id}/password`, values)
      message.success('密码重置成功')
      setResetVisible(false)
      resetForm.resetFields()
    } catch (error) {
      // 错误已在request中处理
    }
  }

  const handleSearch = () => {
    loadUsers(1, pagination.pageSize)
  }

  const handleResetFilters = () => {
    setKeyword('')
    setRoleFilter('all')
    setEnabledFilter('all')
    loadUsers(1, pagination.pageSize, '', 'all', 'all')
  }

  const columns = [
    { title: '用户名', dataIndex: 'username', key: 'username' },
    {
      title: '角色',
      dataIndex: 'role',
      key: 'role',
      render: (role: string, record: UserListItem) => (
        <Select
          value={role}
          style={{ width: 100 }}
          disabled={record.user_id === userId}
          onChange={(value) => handleRoleChange(record.user_id, value)}
        >
          <Select.Option value="admin">管理员</Select.Option>
          <Select.Option value="user">普通用户</Select.Option>
        </Select>
      ),
    },
    {
      title: '状态',
      dataIndex: 'enabled',
      key: 'enabled',
      render: (enabled: boolean, record: UserListItem) => (
        <Space>
          <Switch
            checked={enabled}
            disabled={record.user_id === userId}
            checkedChildren="启用"
            unCheckedChildren="禁用"
            onChange={(checked) => handleEnabledChange(record.user_id, checked)}
          />
          {record.user_id === userId && <Tag>当前账号</Tag>}
        </Space>
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (time: string) => time ? new Date(time).toLocaleString() : '-',
    },
    {
      title: '最后登录',
      dataIndex: 'last_login',
      key: 'last_login',
      render: (time: string) => time ? new Date(time).toLocaleString() : '-',
    },
    {
      title: '操作',
      key: 'action',
      render: (_: any, record: UserListItem) => (
        <Space wrap>
          <Button type="link" icon={<EyeOutlined />} onClick={() => openDetail(record.user_id)}>
            详情
          </Button>
          <Button type="link" icon={<KeyOutlined />} onClick={() => openResetPassword(record)}>
            重置密码
          </Button>
          <Popconfirm
            title="确定删除该用户吗？"
            onConfirm={() => handleDelete(record.user_id)}
            disabled={record.user_id === userId}
          >
            <Button type="link" danger icon={<DeleteOutlined />} disabled={record.user_id === userId}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ]

  return (
    <Card
      title="用户管理"
    >
      <Space wrap style={{ marginBottom: 16 }}>
        <Input
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          placeholder="搜索用户名或用户ID"
          allowClear
          style={{ width: 220 }}
          onPressEnter={handleSearch}
        />
        <Select value={roleFilter} onChange={setRoleFilter} style={{ width: 140 }}>
          <Select.Option value="all">全部角色</Select.Option>
          <Select.Option value="admin">管理员</Select.Option>
          <Select.Option value="user">普通用户</Select.Option>
        </Select>
        <Select value={enabledFilter} onChange={setEnabledFilter} style={{ width: 140 }}>
          <Select.Option value="all">全部状态</Select.Option>
          <Select.Option value="true">已启用</Select.Option>
          <Select.Option value="false">已禁用</Select.Option>
        </Select>
        <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
          查询
        </Button>
        <Button onClick={handleResetFilters}>重置</Button>
        <Button icon={<ReloadOutlined />} onClick={() => loadUsers()}>
          刷新
        </Button>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => {
            createForm.resetFields()
            setCreateVisible(true)
          }}
        >
          创建用户
        </Button>
      </Space>

      <Table
        dataSource={users}
        columns={columns}
        rowKey="user_id"
        loading={loading}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
        }}
        onChange={(pager) => loadUsers(pager.current || 1, pager.pageSize || pagination.pageSize)}
      />

      <Modal
        title="创建用户"
        open={createVisible}
        onCancel={() => setCreateVisible(false)}
        onOk={() => createForm.submit()}
      >
        <Form form={createForm} layout="vertical" onFinish={handleCreate}>
          <Form.Item
            name="username"
            label="用户名"
            rules={[
              { required: true, message: '请输入用户名' },
              { min: 2, message: '用户名至少2个字符' },
              { max: 64, message: '用户名最多64个字符' },
              { pattern: /^[a-zA-Z0-9_-]+$/, message: '用户名只能包含字母、数字、下划线和连字符' },
            ]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="password"
            label="密码"
            rules={[
              { required: true, message: '请输入密码' },
              { min: 6, message: '密码至少6个字符' },
            ]}
          >
            <Input.Password />
          </Form.Item>

          <Form.Item
            name="confirm_password"
            label="确认密码"
            dependencies={['password']}
            rules={[
              { required: true, message: '请确认密码' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) {
                    return Promise.resolve()
                  }
                  return Promise.reject(new Error('两次输入的密码不一致'))
                },
              }),
            ]}
          >
            <Input.Password />
          </Form.Item>

          <Form.Item name="role" label="角色" initialValue="user">
            <Select>
              <Select.Option value="admin">管理员</Select.Option>
              <Select.Option value="user">普通用户</Select.Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="用户详情"
        open={detailVisible}
        footer={null}
        onCancel={() => setDetailVisible(false)}
      >
        {detailLoading ? (
          <Text type="secondary">加载中...</Text>
        ) : (
          <Descriptions bordered column={1} size="small">
            <Descriptions.Item label="用户名">{selectedUser?.username || '-'}</Descriptions.Item>
            <Descriptions.Item label="用户ID">{selectedUser?.user_id || '-'}</Descriptions.Item>
            <Descriptions.Item label="角色">
              {selectedUser?.role === 'admin' ? '管理员' : '普通用户'}
            </Descriptions.Item>
            <Descriptions.Item label="状态">
              {selectedUser?.enabled ? '启用' : '禁用'}
            </Descriptions.Item>
            <Descriptions.Item label="创建时间">
              {selectedUser?.created_at ? new Date(selectedUser.created_at).toLocaleString() : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="最后登录">
              {selectedUser?.last_login ? new Date(selectedUser.last_login).toLocaleString() : '-'}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>

      <Modal
        title={selectedUser ? `重置密码: ${selectedUser.username}` : '重置密码'}
        open={resetVisible}
        onCancel={() => setResetVisible(false)}
        onOk={() => resetForm.submit()}
      >
        <Form form={resetForm} layout="vertical" onFinish={handleResetPassword}>
          <Form.Item
            name="new_password"
            label="新密码"
            rules={[
              { required: true, message: '请输入新密码' },
              { min: 6, message: '密码至少6个字符' },
            ]}
          >
            <Input.Password />
          </Form.Item>
          <Form.Item
            name="confirm_password"
            label="确认新密码"
            dependencies={['new_password']}
            rules={[
              { required: true, message: '请确认新密码' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('new_password') === value) {
                    return Promise.resolve()
                  }
                  return Promise.reject(new Error('两次输入的密码不一致'))
                },
              }),
            ]}
          >
            <Input.Password />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  )
}

// ============================================================
// 报警规则 Tab
// ============================================================

function AlarmRulesTab() {
  const [rules, setRules] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [modalVisible, setModalVisible] = useState(false)
  const [editingRule, setEditingRule] = useState<any>(null)
  const [form] = Form.useForm()

  const loadRules = async () => {
    setLoading(true)
    try {
      const data = await get<any[]>('/api/v1/alarms/rules')
      setRules(data)
    } catch (error) {
      // 错误已在request中处理
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadRules()
  }, [])

  const handleSubmit = async (values: any) => {
    try {
      const payload = {
        ...values,
        config: JSON.parse(values.config || '{}'),
      }

      if (editingRule) {
        await put(`/api/v1/alarms/rules/${editingRule.id}`, payload)
        message.success('规则更新成功')
      } else {
        await post('/api/v1/alarms/rules', payload)
        message.success('规则创建成功')
      }

      setModalVisible(false)
      setEditingRule(null)
      form.resetFields()
      loadRules()
    } catch (error) {
      // 错误已在request中处理
    }
  }

  const handleDelete = async (ruleId: number) => {
    try {
      await del(`/api/v1/alarms/rules/${ruleId}`)
      message.success('规则已删除')
      loadRules()
    } catch (error) {
      // 错误已在request中处理
    }
  }

  const handleToggle = async (ruleId: number, enabled: boolean) => {
    try {
      await put(`/api/v1/alarms/rules/${ruleId}/toggle?enabled=${enabled}`)
      loadRules()
    } catch (error) {
      // 错误已在request中处理
    }
  }

  const openEditModal = (rule: any) => {
    setEditingRule(rule)
    form.setFieldsValue({
      ...rule,
      config: JSON.stringify(rule.config, null, 2),
    })
    setModalVisible(true)
  }

  const columns = [
    { title: '规则名称', dataIndex: 'name', key: 'name' },
    {
      title: '类型',
      dataIndex: 'type',
      key: 'type',
      render: (type: string) => {
        const typeMap: Record<string, string> = {
          target_detect: '目标检测',
          health: '设备健康',
        }
        return typeMap[type] || type
      },
    },
    {
      title: '级别',
      dataIndex: 'level',
      key: 'level',
      render: (level: string) => (
        <Tag color={level === 'danger' ? 'red' : 'orange'}>
          {level === 'danger' ? '危险' : '警告'}
        </Tag>
      ),
    },
    {
      title: '状态',
      dataIndex: 'enabled',
      key: 'enabled',
      render: (enabled: boolean, record: any) => (
        <Switch
          checked={enabled}
          onChange={(checked) => handleToggle(record.id, checked)}
        />
      ),
    },
    {
      title: '操作',
      key: 'action',
      render: (_: any, record: any) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => openEditModal(record)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定删除该规则吗？"
            onConfirm={() => handleDelete(record.id)}
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ]

  return (
    <Card
      title="报警规则"
      extra={
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => {
            setEditingRule(null)
            form.resetFields()
            setModalVisible(true)
          }}
        >
          创建规则
        </Button>
      }
    >
      <Table
        dataSource={rules}
        columns={columns}
        rowKey="id"
        loading={loading}
        pagination={false}
      />

      <Modal
        title={editingRule ? '编辑规则' : '创建规则'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false)
          setEditingRule(null)
        }}
        onOk={() => form.submit()}
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="name"
            label="规则名称"
            rules={[{ required: true, message: '请输入规则名称' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item name="description" label="描述">
            <Input />
          </Form.Item>

          <Form.Item
            name="type"
            label="规则类型"
            rules={[{ required: true, message: '请选择规则类型' }]}
          >
            <Select>
              <Select.Option value="target_detect">目标检测</Select.Option>
              <Select.Option value="health">设备健康</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="config"
            label="规则配置 (JSON)"
            rules={[
              { required: true, message: '请输入规则配置' },
              {
                validator: (_, value) => {
                  try {
                    JSON.parse(value || '{}')
                    return Promise.resolve()
                  } catch {
                    return Promise.reject('JSON 格式错误')
                  }
                },
              },
            ]}
          >
            <TextArea rows={4} placeholder='{"target_types": [0], "min_confidence": 0.7}' />
          </Form.Item>

          <Form.Item name="level" label="报警级别" initialValue="warning">
            <Select>
              <Select.Option value="warning">警告</Select.Option>
              <Select.Option value="danger">危险</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item name="enabled" label="启用" valuePropName="checked" initialValue={true}>
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  )
}

// ============================================================
// 系统配置 Tab
// ============================================================

function SystemTab() {
  const [mapCenter, setMapCenter] = useState({ lon: 116.397428, lat: 39.90923, zoom: 12 })
  const [registrationEnabled, setRegistrationEnabled] = useState(true)
  const [loading, setLoading] = useState(false)
  const [form] = Form.useForm()

  // 获取全局状态的 setMapCenter
  const setGlobalMapCenter = useUIStore((state) => state.setMapCenter)

  const loadMapCenter = async () => {
    try {
      // 加载地图中心
      const data = await get<{ lon: number; lat: number; zoom: number }>('/api/v1/settings/map-center')
      setMapCenter(data)
      form.setFieldsValue(data)
      // 同步到全局状态
      setGlobalMapCenter(data)

      // 加载注册开关
      const regData = await get<{ value: { enabled: boolean } }>('/api/v1/settings/system/registration_enabled')
      setRegistrationEnabled(regData.value.enabled)
    } catch (error) {
      console.error('加载设置失败')
    }
  }

  useEffect(() => {
    loadMapCenter()
  }, [])

  const handleSaveMapCenter = async (values: any) => {
    setLoading(true)
    try {
      await put('/api/v1/settings/map-center', values)
      message.success('地图中心已保存')
      setMapCenter(values)
      // 更新全局状态，触发地图组件响应
      setGlobalMapCenter(values)
    } catch (error) {
      // 错误已在request中处理
    } finally {
      setLoading(false)
    }
  }

  // 处理注册开关切换
  const handleRegistrationToggle = async (checked: boolean) => {
    try {
      await put('/api/v1/settings/system/registration_enabled', { enabled: checked })
      setRegistrationEnabled(checked)
      message.success(checked ? '已开启用户注册' : '已关闭用户注册')
    } catch (error) {
      // 错误已在request中处理
    }
  }

  return (
    <Row gutter={16}>
      <Col span={12}>
        <Card title="地图中心设置" extra={<EnvironmentOutlined />}>
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSaveMapCenter}
            initialValues={mapCenter}
          >
            <Form.Item
              name="lon"
              label="经度"
              rules={[
                { required: true, message: '请输入经度' },
                { type: 'number', min: -180, max: 180, message: '经度范围 -180 到 180' },
              ]}
            >
              <InputNumber style={{ width: '100%' }} step={0.0001} precision={6} />
            </Form.Item>

            <Form.Item
              name="lat"
              label="纬度"
              rules={[
                { required: true, message: '请输入纬度' },
                { type: 'number', min: -90, max: 90, message: '纬度范围 -90 到 90' },
              ]}
            >
              <InputNumber style={{ width: '100%' }} step={0.0001} precision={6} />
            </Form.Item>

            <Form.Item
              name="zoom"
              label="缩放级别"
              rules={[
                { required: true, message: '请输入缩放级别' },
                { type: 'number', min: 1, max: 20, message: '缩放级别范围 1 到 20' },
              ]}
            >
              <InputNumber style={{ width: '100%' }} min={1} max={20} />
            </Form.Item>

            <Form.Item>
              <Button type="primary" htmlType="submit" loading={loading}>
                保存设置
              </Button>
            </Form.Item>
          </Form>

          <Divider />

          <Text type="secondary">
            当前中心: {(mapCenter?.lon || 0).toFixed(6)}, {(mapCenter?.lat || 0).toFixed(6)} (缩放: {mapCenter?.zoom || 12})
          </Text>
        </Card>
      </Col>

      <Col span={12}>
        <Card title="系统信息">
          <Space direction="vertical" style={{ width: '100%' }}>
            <div>
              <Text type="secondary">系统版本</Text>
              <div><Text strong>1.0.0</Text></div>
            </div>
            <div>
              <Text type="secondary">API 地址</Text>
              <div><Text code>{API_BASE}</Text></div>
            </div>
          </Space>
        </Card>

        <Card title="注册功能设置" style={{ marginTop: 16 }}>
          <Space direction="vertical" style={{ width: '100%' }}>
            <div>
              <Space>
                <Text>允许新用户注册</Text>
                <Switch
                  checked={registrationEnabled}
                  onChange={handleRegistrationToggle}
                  checkedChildren="开启"
                  unCheckedChildren="关闭"
                />
              </Space>
            </div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              关闭后，登录页面的注册功能将被禁用
            </Text>
          </Space>
        </Card>
      </Col>
    </Row>
  )
}

// ============================================================
// 主组件
// ============================================================

function Settings() {
  const { isAdmin } = useUserStore()
  const isAdminUser = isAdmin()

  const items = [
    {
      key: 'profile',
      label: (
        <span>
          <UserOutlined />
          个人信息
        </span>
      ),
      children: <ProfileTab />,
    },
    {
      key: 'users',
      label: (
        <span>
          <TeamOutlined />
          用户管理
        </span>
      ),
      children: <UserManageTab />,
      disabled: !isAdminUser,
    },
    {
      key: 'alarms',
      label: (
        <span>
          <BellOutlined />
          报警规则
        </span>
      ),
      children: <AlarmRulesTab />,
      disabled: !isAdminUser,
    },
    {
      key: 'system',
      label: (
        <span>
          <SettingOutlined />
          系统设置
        </span>
      ),
      children: <SystemTab />,
      disabled: !isAdminUser,
    },
  ]

  return (
    <div className="page-shell">
      <div className="page-scroll">
        <div style={{ padding: '0 12px' }}>
          <Tabs items={items} defaultActiveKey="profile" />
        </div>
      </div>
    </div>
  )
}

export default Settings
